/**
 * Сессия входа Front — экземпляр рантайма `@srm/core/auth`. Создание ничего не делает: подписки, канал вкладок и
 * таймеры запускает `startAuth(store)` (index.tsx) после создания стора. Путь `@/auth` — модуль-экземпляр: его
 * импортируют http-клиент, стор и компоненты.
 */
import { createAuthRuntime, FRONT_AUTH_NAMESPACE, SAML_LOGIN_TYPE, type AuthReauthOptions } from '@srm/core/auth';
import { normalizeBasePath } from '@srm/core/routing';
import { CLIENT_VERSION_HEADER } from '@srm/core/version';
import { BASE_PATH } from '@/configuration';
import { APP_VERSION } from '@/configuration/appVersion';
import { ConfigService } from '@/configuration/services/configService';
import { supportShareHeaders } from '@/http/supportShareHeader';
import { RouteNames } from '@/router/router.types';
import {
    clearSupportSession,
    getSupportSession,
    isSupportSession,
    saveSupportSession,
    SUPPORT_HIDDEN_BEAT_INTERVAL_MS,
} from './supportSession';

/** Страницы входа и выхода через IdP (`/sso/saml/…`): повторный вход отсюда увёл бы на IdP снова — петля. */
const onSamlPage = () =>
    window.location.pathname.toLowerCase().startsWith(`${normalizeBasePath(BASE_PATH)}/sso/saml/`.toLowerCase());

/**
 * Повторный вход через IdP, когда сервер отвечает на ротацию `reauth_required` (потолок жизни SAML-семейства —
 * единственный момент, когда блокировка учётки в IdP доходит до уже выданных токенов). Внутренний SAML-редиректор
 * сохранит returnUrl и уйдёт на IdP; returnUrl санитизирует он сам.
 */
export const samlReauth: AuthReauthOptions = {
    // Исключение (конфиг недоступен) рантайм читает как «выключен».
    enabled: () =>
        !onSamlPage() && ConfigService.get().application.authentication.controls.enabled.includes(SAML_LOGIN_TYPE),
    redirectUrl(currentPath) {
        const params = new URLSearchParams();
        if (currentPath && currentPath !== '/') params.set('returnUrl', currentPath);
        params.set('rememberMe', 'true');
        return `${RouteNames.ADFS_SAML_SSO_SIGN_IN_REDIRECT}?${params.toString()}`;
    },
};

// Конфиг читается при импорте, как у http-клиента. `?.` — для тестов, подменяющих конфиг без `application`.
const application = ConfigService.get()?.application;

export const auth = createAuthRuntime({
    namespace: FRONT_AUTH_NAMESPACE,
    apiUrl: application?.apiUrl ?? '',
    withCredentials: application?.withCredentials ?? false,
    reauth: samlReauth,
    // SignOut идёт транспортом сессии, мимо плагинов клиента: версия и показ экрана — здесь.
    signOutHeaders: () => ({ [CLIENT_VERSION_HEADER]: APP_VERSION, ...supportShareHeaders() }),
    // Вкладка «от имени» (сеансы поддержки): токен только в sessionStorage вкладки, источник — supportSession.ts.
    isolated: {
        active: () => isSupportSession(),
        getAccessToken: () => getSupportSession()?.accessToken ?? null,
        setAccessToken(accessToken) {
            const session = getSupportSession();
            if (session) saveSupportSession({ ...session, accessToken });
        },
        clear: () => clearSupportSession(),
        hiddenBeatIntervalMs: SUPPORT_HIDDEN_BEAT_INTERVAL_MS,
    },
});

// Горячая замена модуля: прежний экземпляр освобождает namespace, канал и таймеры (иначе новый упадёт на реестре).
import.meta.hot?.dispose(() => auth.stop());
